import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Snackbar
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Email as EmailIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import {
  fetchReferrals,
  fetchReferralStats,
  generateNewReferralLink,
  fetchReferralActivity,
  fetchReferralAnalytics,
  selectReferralActivity,
  selectReferralAnalytics
} from '../../store/slices/referralsSlice';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  BarChart,
  Bar
} from 'recharts';

const rewardRules = [
  {
    action: 'Регистрация по бонусному коду',
    points: '0.3 + 0.3',
    description: 'Вы и реферал получите по 0.3 балла при регистрации'
  },
  {
    action: 'Первый завершенный опрос',
    points: 0.2,
    description: 'Вы получите 0.2 балла когда ваш реферал завершит свой первый опрос'
  }
];

// Translation map for referral reasons
const translateReason = (reason) => {
  const reasonMap = {
    'registration_completed': 'Регистрация и заполнение профиля',
    'first_survey_completed': 'Первый завершенный опрос',
    'Registration and Profile Completion': 'Регистрация и заполнение профиля',
    'First survey completed': 'Первый завершенный опрос',
    'referral_activity': 'Реферальная активность',
    'milestone': 'Достижение'
  };
  return reasonMap[reason] || reason;
};

export default function Referrals() {
  const dispatch = useDispatch();
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyCodeSuccess, setCopyCodeSuccess] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  
  const { referrals, stats, referralLink, loading, error } = useSelector((state) => state.referrals);
  const activity = useSelector(selectReferralActivity);
  const analytics = useSelector(selectReferralAnalytics);
  
  // Extract referral code from link
  const referralCode = referralLink ? referralLink.split('/').pop() : '';
  
  useEffect(() => {
    dispatch(fetchReferrals());
    dispatch(fetchReferralStats());
    dispatch(generateNewReferralLink());
    dispatch(fetchReferralAnalytics());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchReferralActivity({ page: activityPage, limit: 10 }));
  }, [dispatch, activityPage]);

  const totalPointsEarned = stats.totalPoints;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopyCodeSuccess(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = (platform) => {
    const message = encodeURIComponent(
      'Присоединяйтесь к платформе опросов ThinkLink и зарабатывайте награды, делясь своим мнением! Регистрируйтесь по моей реферальной ссылке:'
    );
    
    let shareUrl = '';
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${message} ${referralLink}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${referralLink}&text=${message}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=Присоединяйтесь к ThinkLink Survey Platform&body=${message} ${referralLink}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank');
  };

  const loadActivityPage = (page) => {
    setActivityPage(page);
    dispatch(fetchReferralActivity({ page, limit: 10 }));
  };

  return (
    <Box>
      {/* Referral Link Section */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 1.5, md: 4 },
          mx: { xs: 0, md: 3 },
          mt: 0,
          mb: 2,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '24px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3,
            fontWeight: 700,
            color: '#2d3748'
          }}
        >
          Приглашайте друзей и зарабатывайте награды
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Ваш бонусный код:
            </Typography>
            <TextField
              fullWidth
              value={referralCode}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton onClick={handleCopyCode}>
                    <CopyIcon />
                  </IconButton>
                ),
              }}
              sx={{ mb: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Ваша реферальная ссылка:
            </Typography>
            <TextField
              fullWidth
              value={referralLink}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton onClick={handleCopyLink}>
                    <CopyIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Tooltip title="Поделиться в WhatsApp">
                <IconButton 
                  color="success" 
                  onClick={() => handleShare('whatsapp')}
                >
                  <WhatsAppIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Поделиться в Telegram">
                <IconButton 
                  color="primary" 
                  onClick={() => handleShare('telegram')}
                >
                  <TelegramIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Поделиться по Email">
                <IconButton 
                  color="secondary" 
                  onClick={() => handleShare('email')}
                >
                  <EmailIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Rewards Summary */}
      <Grid container spacing={2} sx={{ mb: 2, px: { xs: 0, md: 3 } }}>
        <Grid item xs={12}>
          <Card 
            elevation={0}
            sx={{
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2d3748' }}>
                Правила начисления баллов
              </Typography>
              <Grid container spacing={2}>
                {rewardRules.map((rule, index) => (
                  <Grid item xs={12} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6" color="primary">
                        {rule.points}
                      </Typography>
                      <Box>
                        <Typography variant="subtitle1">
                          {rule.action}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {rule.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Referrals List */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 1.5, md: 4 },
          mx: { xs: 0, md: 3 },
          mb: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '8px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 2,
            fontWeight: 700,
            color: '#2d3748'
          }}
        >
          Ваши рефералы
        </Typography>
        
        {error?.referrals && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.referrals}
          </Alert>
        )}
        
        <TableContainer 
          sx={{ 
            borderRadius: '24px',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)' }}>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Имя</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Статус</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Дата регистрации</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Опросов завершено</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#2d3748', fontSize: '0.95rem' }}>Заработано баллов</TableCell>
              </TableRow>
            </TableHead>
            {loading?.referrals ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
              {referrals.map((referral) => (
                <TableRow 
                  key={referral.id}
                  sx={{
                    '&:hover': {
                      background: 'rgba(116, 185, 255, 0.05)',
                    },
                    transition: 'background 0.2s'
                  }}
                >
                  <TableCell sx={{ color: '#2d3748', fontWeight: 500 }}>{referral.name}</TableCell>
                  <TableCell sx={{ color: '#718096' }}>{referral.email}</TableCell>
                  <TableCell>
                    <Box 
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 2,
                        py: 0.5,
                        borderRadius: '12px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        ...(referral.status === 'completed_survey' && {
                          background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                          color: '#fff',
                        }),
                        ...(referral.status !== 'completed_survey' && {
                          background: 'rgba(0, 0, 0, 0.08)',
                          color: '#718096',
                        })
                      }}
                    >
                      {referral.status === 'completed_survey' && <CheckIcon fontSize="small" />}
                      {referral.status === 'completed_survey' 
                        ? 'Завершил(а) опрос' 
                        : 'Зарегистрирован(а)'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#718096' }}>{referral.joinedDate}</TableCell>
                  <TableCell sx={{ color: '#2d3748', fontWeight: 600 }}>{referral.completedSurveys}</TableCell>
                  <TableCell align="right" sx={{ color: '#2d3748', fontWeight: 700 }}>{Number(referral.pointsEarned || 0).toFixed(1)}</TableCell>
                </TableRow>
              ))}
              {referrals.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      Пока нет рефералов. Поделитесь ссылкой, чтобы начать зарабатывать!
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Paper>

      {/* Activity & Analytics */}
      <Grid container spacing={2} sx={{ mt: 1, px: { xs: 0, md: 3 } }}>
        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2d3748' }}>
              История реферальной активности
            </Typography>
            {loading?.activity ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Дата</TableCell>
                        <TableCell>Действие</TableCell>
                        <TableCell>Баллы</TableCell>
                        <TableCell>Реферал</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(activity?.data || []).map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{new Date(row.createdAt).toLocaleDateString('ru-RU', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</TableCell>
                          <TableCell>{translateReason(row.reason)}</TableCell>
                          <TableCell>+{Number(row.points || 0).toFixed(1)}</TableCell>
                          <TableCell>{row.referredUser?.fullName || row.referredUser?.email || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button disabled={activity?.page <= 1} onClick={() => loadActivityPage((activity?.page || 1) - 1)}>Назад</Button>
                  <Typography variant="body2">Страница {activity?.page || 1} — всего {activity?.total || 0}</Typography>
                  <Button disabled={(activity?.page || 1) * (activity?.limit || 10) >= (activity?.total || 0)} onClick={() => loadActivityPage((activity?.page || 1) + 1)}>Вперед</Button>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setCopySuccess(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          Реферальная ссылка скопирована!
        </Alert>
      </Snackbar>

      <Snackbar
        open={copyCodeSuccess}
        autoHideDuration={3000}
        onClose={() => setCopyCodeSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setCopyCodeSuccess(false)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          Бонусный код скопирован!
        </Alert>
      </Snackbar>
    </Box>
  );
}